import { useCallback, useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  REALTIME_CONNECTION_STATES,
  subscribeNotificationInboxChanged,
  subscribeRealtimeConnectionState,
} from "../utils/notificationRecovery";

const GET_UNREAD_NOTIFICATIONS = gql`
  query GetUnreadNotifications {
    myNotifications(limit: 50, unreadOnly: true) {
      id
    }
  }
`;

const GET_UNREAD_MESSAGES = gql`
  query GetUnreadMessages {
    myConversations(limit: 50) {
      id
      unreadCount
    }
  }
`;

const GET_INCOMING_REQUESTS = gql`
  query GetIncomingRequests {
    myIncomingRequests {
      id
      from {
        id
        username
        avatarUrl
      }
    }
  }
`;

export default function useMenuCounts(user) {
  const {
    data: unreadNotificationsData,
    refetch: refetchUnreadNotifications,
  } = useQuery(GET_UNREAD_NOTIFICATIONS, {
    skip: !user,
    fetchPolicy: "network-only",
  });

  const {
    data: unreadMessagesData,
    refetch: refetchUnreadMessages,
  } = useQuery(GET_UNREAD_MESSAGES, {
    skip: !user,
    fetchPolicy: "network-only",
  });

  const {
    data: incomingRequestsData,
    refetch: refetchIncomingRequests,
  } = useQuery(GET_INCOMING_REQUESTS, {
    skip: !user,
    fetchPolicy: "network-only",
  });
  const refreshCounts = useCallback(() => {
    if (!user) return;
    Promise.allSettled([
      refetchUnreadNotifications(),
      refetchUnreadMessages(),
      refetchIncomingRequests(),
    ]);
  }, [user, refetchUnreadNotifications, refetchUnreadMessages, refetchIncomingRequests]);

  useEffect(() => subscribeNotificationInboxChanged(() => {
    if (user) refetchUnreadNotifications().catch(() => {});
  }), [user, refetchUnreadNotifications]);

  useEffect(() => subscribeRealtimeConnectionState((state) => {
    if (state === REALTIME_CONNECTION_STATES.CONNECTED) refreshCounts();
  }), [refreshCounts]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => refreshCounts();
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [refreshCounts]);

  const unreadNotifications = unreadNotificationsData?.myNotifications?.length || 0;
  const unreadMessages =
    unreadMessagesData?.myConversations?.reduce(
      (sum, conversation) => sum + (conversation.unreadCount || 0),
      0
    ) || 0;

  const pendingRequestUsers =
    incomingRequestsData?.myIncomingRequests?.map((request) => request.from) || [];

  return {
    unreadNotifications,
    unreadMessages,
    pendingRequests: pendingRequestUsers.length,
    pendingRequestUsers,
  };
}
